import React, { useState, useEffect } from 'react';
import { FileText, Download, Mail, Calendar, User, X } from 'lucide-react';
import { apiService } from '../services/api';

interface ScanRecord {
  id: string;
  fullName: string;
  email: string;
  timestamp: string;
  fileNames: string[];
  pdfUrl?: string;
}

interface ScanHistoryProps {
  onClose: () => void;
  onSelectSession?: (sessionId: string) => void;
}

const ScanHistory: React.FC<ScanHistoryProps> = ({ onClose }) => {
  const [scanRecords, setScanRecords] = useState<ScanRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScanRecords();
  }, []);

  const loadScanRecords = async () => {
    try {
      setLoading(true);
      const records = await apiService.getScanRecords(50);
      setScanRecords(records);
    } catch (error) {
      console.error('Failed to load scan records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (record: ScanRecord) => {
    if (!record.pdfUrl || !record.email) return;
    
    try {
      await apiService.sendPDFEmail(record.email, record.pdfUrl, record.fullName);
      alert(`PDF report has been resent to ${record.email}`);
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Failed to send email. Please try again.');
    }
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
      <div className="h-full bg-white flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-medical-200 px-6 py-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="w-8 h-8 text-primary-600" />
                <div>
                  <h1 className="text-2xl font-semibold text-medical-900">Scan History</h1>
                  <p className="text-medical-600">View and manage previous scan reports</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-medical-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-medical-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-medical-50">
          <div className="max-w-6xl mx-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent"></div>
              </div>
            ) : scanRecords.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-medical-400 mx-auto mb-4" />
                <p className="text-medical-600">No scan records found</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {scanRecords.map((record) => (
                  <div
                    key={record.id}
                    className="bg-white rounded-lg shadow-sm border border-medical-200 p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4 text-medical-600" />
                            <span className="font-medium text-medical-900">
                              {record.fullName || 'Anonymous'}
                            </span>
                          </div>
                          {record.email && (
                            <div className="flex items-center space-x-2">
                              <Mail className="w-4 h-4 text-medical-600" />
                              <span className="text-medical-600">{record.email}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2 text-sm text-medical-500 mb-3">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(record.timestamp)}</span>
                        </div>
                        
                        <div className="text-sm text-medical-600">
                          <span className="font-medium">Files analyzed:</span> {record.fileNames.join(', ')}
                        </div>
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        {record.pdfUrl && (
                          <>
                            <a
                              href={record.pdfUrl}
                              download
                              className="p-2 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4 text-primary-600" />
                            </a>
                            {record.email && (
                              <button
                                onClick={() => handleResendEmail(record)}
                                className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                title="Resend Email"
                              >
                                <Mail className="w-4 h-4 text-blue-600" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanHistory;